import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  Trash2,
  Save,
  Loader2,
  GripVerticalIcon,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import Loader from "../Loader";
import PageHeader from "../Common/PageHeader";
import useToastStore from "../Common/Toast/toastStore";
import { vacancyStatusTemplateService } from "@/services/vacancyStatusTemplateService";

const StageBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToastStore;
  const isEditMode = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [stages, setStages] = useState([]);
  const [newStageName, setNewStageName] = useState("");
  const [selectedColor, setSelectedColor] = useState("#FF0000");
  const [templateName, setTemplateName] = useState("");

  // Load existing template if editing
  useEffect(() => {
    if (isEditMode && id) {
      const loadTemplate = async () => {
        setIsLoading(true);
        try {
          const response =
            await vacancyStatusTemplateService.getTemplateById(id);
          const template = response.data || response;

          if (template) {
            setTemplateName(template.title || "");
            if (template.status && Array.isArray(template.status)) {
              const loadedStages = template.status.map((status, index) => ({
                id: status.id || uuidv4(),
                name: status.name,
                color: status.bg_color || "#FF0000",
                p_order: status.p_order || index + 1,
              }));
              setStages(loadedStages);
            }
          }
        } catch (error) {
          showToast({
            message: error.message || "Failed to load template",
            type: "error",
          });
        } finally {
          setIsLoading(false);
        }
      };

      loadTemplate();
    }
  }, [id, isEditMode]);

  const handleAddStage = () => {
    if (!newStageName.trim()) {
      showToast({
        message: "Please enter a stage name",
        type: "error",
      });
      return;
    }

    const newStage = {
      id: uuidv4(),
      name: newStageName.trim(),
      color: selectedColor,
      p_order: stages.length + 1,
    };

    setStages((prev) => [...prev, newStage]);
    setNewStageName("");
    setSelectedColor("#FF0000");
  };

  const handleDeleteStage = (stageId) => {
    setStages((prev) => prev.filter((stage) => stage.id !== stageId));
  };

  const handleUpdateStageName = (stageId, newName) => {
    setStages((prev) =>
      prev.map((stage) =>
        stage.id === stageId ? { ...stage, name: newName } : stage,
      ),
    );
  };

  const handleUpdateStageColor = (stageId, newColor) => {
    setStages((prev) =>
      prev.map((stage) =>
        stage.id === stageId ? { ...stage, color: newColor } : stage,
      ),
    );
  };

  const handleMoveStage = (stageId, direction) => {
    const index = stages.findIndex((stage) => stage.id === stageId);
    const newStages = [...stages];

    if (direction === "up" && index > 0) {
      const [movedStage] = newStages.splice(index, 1);
      newStages.splice(index - 1, 0, movedStage);
    } else if (direction === "down" && index < stages.length - 1) {
      const [movedStage] = newStages.splice(index, 1);
      newStages.splice(index + 1, 0, movedStage);
    }

    // Update p_order
    newStages.forEach((stage, i) => {
      stage.p_order = i + 1;
    });

    setStages(newStages);
  };

  const handleReorderStages = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const reorderedStages = Array.from(stages);
    const [movedStage] = reorderedStages.splice(source.index, 1);
    reorderedStages.splice(destination.index, 0, movedStage);

    // Update p_order
    reorderedStages.forEach((stage, i) => {
      stage.p_order = i + 1;
    });

    setStages(reorderedStages);
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      showToast({
        message: "Please enter a template name",
        type: "error",
      });
      return;
    }

    if (stages.length === 0) {
      showToast({
        message: "Please add at least one stage",
        type: "error",
      });
      return;
    }

    setIsSaving(true);

    try {
      // Prepare payload according to API specification
      const payload = {
        title: templateName.trim(),
        status: stages.map((stage) => ({
          name: stage.name,
          bg_color: stage.color,
          p_order: stage.p_order,
        })),
      };

      if (isEditMode && id) {
        // Update existing template
        await vacancyStatusTemplateService.updateTemplate(id, payload);
        showToast({
          message: "Template updated successfully!",
          type: "success",
        });
      } else {
        // Create new template
        await vacancyStatusTemplateService.createTemplate(payload);
        showToast({
          message: "Template created successfully!",
          type: "success",
        });
      }

      // Navigate back to vacancy statuses page
      setTimeout(() => {
        navigate("/vacancy/Statuses");
      }, 1000);
    } catch (error) {
      showToast({
        message: error.message || "Failed to save template",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/vacancy/Statuses");
  };

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading stage configurations..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12  px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                {isEditMode ? "Edit Stages" : "Add Stages"}
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                {isEditMode
                  ? "Modify existing stages and lifecycle steps for this vacancy status template."
                  : "Define custom stages and progression steps for your vacancy status template."}
              </p>
            </div>
          </div>
        </div>

        <PageHeader
          navigationText="Back to Vacancy Statuses"
          navigateTo={handleBack}
        />

        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="p-6 space-y-6">
            {/* Template Name */}
            <div className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="templateName"
                  className="block text-foreground font-medium"
                >
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="templateName"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-muted text-foreground"
                  placeholder="Enter template name"
                />
              </div>
            </div>

            {/* Stages Section */}
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground mb-4">Stages</h2>

              <DragDropContext onDragEnd={handleReorderStages}>
                <Droppable droppableId="stages">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="space-y-2"
                    >
                      {stages.length > 0 ? (
                        stages.map((stage, index) => (
                          <Draggable
                            key={stage.id}
                            draggableId={stage.id}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="flex justify-between items-center p-2 border rounded-lg bg-muted"
                              >
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: stage.color }}
                                  />
                                  <input
                                    type="text"
                                    value={stage.name}
                                    onChange={(e) =>
                                      handleUpdateStageName(
                                        stage.id,
                                        e.target.value,
                                      )
                                    }
                                    className="w-auto bg-transparent focus:outline-none text-foreground border-transparent focus:border-primary px-1"
                                    placeholder="Stage name"
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="color"
                                    value={stage.color || "#FFFFFF"}
                                    onChange={(e) =>
                                      handleUpdateStageColor(
                                        stage.id,
                                        e.target.value,
                                      )
                                    }
                                    className="appearance-none w-6 h-6 rounded-full cursor-pointer p-0 bg-transparent"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleMoveStage(stage.id, "up")
                                    }
                                    className="text-foreground hover:text-primary transition-colors"
                                    disabled={index === 0}
                                  >
                                    <ChevronUp className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleMoveStage(stage.id, "down")
                                    }
                                    className="text-foreground hover:text-primary transition-colors"
                                    disabled={index === stages.length - 1}
                                  >
                                    <ChevronDown className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteStage(stage.id)}
                                    className="text-red-500 hover:text-red-700 transition-colors"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                  <button
                                    type="button"
                                    {...provided.dragHandleProps}
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                  >
                                    <GripVerticalIcon className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <div className="text-center text-muted-foreground py-8 border border-input rounded-lg bg-muted">
                          <p>
                            No stages added yet. Add your first stage below.
                          </p>
                        </div>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {/* Add Stage Form */}
              <div className="flex items-center gap-4 p-2 border border-input rounded-lg bg-muted">
                <input
                  type="text"
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddStage();
                    }
                  }}
                  placeholder="Enter stage name"
                  className="flex-1 px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-card text-foreground"
                />
                <input
                  type="color"
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  className="appearance-none w-10 h-10 rounded-full cursor-pointer "
                />
                <button
                  type="button"
                  onClick={handleAddStage}
                  className="bg-primary-dark hover:bg-primary-dark transition-colors text-white rounded-lg px-6 py-2 font-medium"
                >
                  Add Stage
                </button>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-4 pt-4 border-t">
              <button
                type="button"
                onClick={handleBack}
                className="px-6  border border-input rounded-lg h-[36px] text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 bg-primary-dark hover:bg-primary-dark transition-colors h-[36px] text-white rounded-lg px-6  font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {isEditMode ? "Update Stages" : "Save Stages"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StageBuilder;
